import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import Image, { ImageProps } from '@components/image';
import getLinkConfig from '@helpers/get-link-config';
import {
  OverlayContainer,
  Backdrop,
  OverlayCard,
  CloseButton,
  OverlayAnchor,
  OverlayGatsbyLink,
  ImageWrapper,
} from './notification-overlay.styled';

const DISMISS_COOKIE_NAME = 'notificationOverlayDismissed';

interface NotificationOverlayLink {
  url?: string;
  uid?: string;
  target?: string;
}

interface NotificationOverlayProps {
  image?: ImageProps;
  link?: NotificationOverlayLink;
  linkLabel?: string;
  isDismissible?: boolean;
}

const NotificationOverlay = ({
  image,
  link,
  linkLabel,
  isDismissible = true,
}: NotificationOverlayProps): JSX.Element | null => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasScrolledPast, setHasScrolledPast] = useState(false);
  const linkConfig = useMemo(() => getLinkConfig(link), [link?.uid, link?.url]);
  const hasImage = Boolean(image?.gatsbyImageData || image?.url);
  const overlayIdentifier = useMemo(
    () =>
      `notification-overlay-${
        linkConfig.url || image?.url || image?.gatsbyImageData?.images?.fallback?.src || 'default'
      }`,
    [image?.gatsbyImageData?.images?.fallback?.src, image?.url, linkConfig.url]
  );

  useEffect(() => {
    if (!isDismissible) {
      setIsDismissed(false);
      return;
    }

    const dismissedId = Cookies.get(DISMISS_COOKIE_NAME);
    setIsDismissed(dismissedId === overlayIdentifier);
  }, [overlayIdentifier, isDismissible]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (!hasImage || (isDismissible && isDismissed) || hasScrolledPast) {
      return undefined;
    }

    const detectScroll = () => {
      if (window.scrollY >= window.innerHeight) {
        setHasScrolledPast(true);
      }
    };

    detectScroll();
    window.addEventListener('scroll', detectScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', detectScroll);
    };
  }, [hasImage, hasScrolledPast, isDismissed, isDismissible]);

  const handleClose = useCallback(() => {
    if (!isDismissible) {
      return;
    }

    setIsDismissed(true);
    Cookies.set(DISMISS_COOKIE_NAME, overlayIdentifier, { expires: 7 });
  }, [isDismissible, overlayIdentifier]);

  if (!hasImage) {
    return null;
  }

  if (!hasScrolledPast) {
    return null;
  }

  if (isDismissible && isDismissed) {
    return null;
  }

  const ariaLabel = linkLabel || image?.alt || 'Notification overlay link destination';

  let content: JSX.Element;

  if (linkConfig.url) {
    if (linkConfig.isExternal) {
      content = (
        <OverlayAnchor
          href={linkConfig.url}
          target={link?.target}
          rel={link?.target === '_blank' ? 'noopener noreferrer' : undefined}
          aria-label={ariaLabel}
        >
          <ImageWrapper>
            <Image
              gatsbyImageData={image?.gatsbyImageData}
              url={image?.url}
              alt={image?.alt || ''}
              objectFit="cover"
            />
          </ImageWrapper>
        </OverlayAnchor>
      );
    } else {
      content = (
        <OverlayGatsbyLink to={linkConfig.url} aria-label={ariaLabel}>
          <ImageWrapper>
            <Image
              gatsbyImageData={image?.gatsbyImageData}
              url={image?.url}
              alt={image?.alt || ''}
              objectFit="cover"
            />
          </ImageWrapper>
        </OverlayGatsbyLink>
      );
    }
  } else {
    content = (
      <ImageWrapper>
        <Image
          gatsbyImageData={image?.gatsbyImageData}
          url={image?.url}
          alt={image?.alt || ''}
          objectFit="cover"
        />
      </ImageWrapper>
    );
  }

  return (
    <OverlayContainer role="dialog" aria-modal="true">
      <Backdrop
        onClick={handleClose}
        $isInteractive={isDismissible}
        aria-hidden="true"
        data-testid="notification-overlay-backdrop"
      />
      <OverlayCard>
        {isDismissible && (
          <CloseButton
            type="button"
            onClick={handleClose}
            aria-label="Close notification overlay"
          />
        )}
        {content}
      </OverlayCard>
    </OverlayContainer>
  );
};

export default NotificationOverlay;
